import type { MatrixPos, Priority, RuleInput } from "@app/core";
import { effectivePriorityLevel, lastOccurrence, parseRRule, resolveEffectiveRrule, sameMatrixPos, systemTimeZone, toRRuleString } from "@app/core";
import { useEffect, useMemo, useRef, useState } from "react";
import { tr } from "../../lib/tr.js";
import { occurrenceRefOf } from "../../lib/virtual-id.js";
import { toast, useRecurringConfirmStore, useSettingsStore, useTaskDialogStore, useTasksStore } from "../../stores/index.js";
import { matrixEnabledFor } from "../../stores/settings.store.js";
import { beginUndoable, endUndoable, undoAction } from "../../stores/undo.js";
import { useToday } from "../calendar-view/useNowMinute.js";
import { submitTaskForm } from "./submitTaskForm.js";
import { formDeadlineIso, toLocalDate, wallClockOf } from "./time-helpers.js";

export function defaultReminderOffsetsForNewItem(
  isEvent: boolean,
  defaultTaskReminderMinutes: number[],
  defaultEventReminderMinutes: number[],
): number[] {
  return isEvent ? defaultEventReminderMinutes : defaultTaskReminderMinutes;
}

function sameOffsets(a: number[], b: number[]): boolean {
  return a.length === b.length && a.every((v, i) => v === b[i]);
}

export function reminderOffsetsForNewItemKindToggle(
  nextIsEvent: boolean,
  currentOffsets: number[],
  defaultTaskReminderMinutes: number[],
  defaultEventReminderMinutes: number[],
): number[] {
  // Switching kinds only re-seeds the reminders the user hasn't customized away
  // from the OTHER kind's default; bespoke offsets are preserved.
  if (nextIsEvent) {
    return sameOffsets(currentOffsets, defaultTaskReminderMinutes) ? defaultEventReminderMinutes : currentOffsets;
  }
  return currentOffsets.length === 0 || sameOffsets(currentOffsets, defaultEventReminderMinutes)
    ? defaultTaskReminderMinutes
    : currentOffsets;
}

interface CreateDraftSnapshot {
  title: string;
  notes: string;
  dateStr: string;
  hour: number | null;
  minute: number | null;
  allDay: boolean;
  duration: number;
  priority: Priority;
  matrixPos: MatrixPos | null;
  matrixHidden: boolean;
  customColor: string | null;
  isEvent: boolean;
  location: string;
  reminderOffsets: number[];
  tz: string | null;
  calendarId: string | null;
  topicIds: string[];
  labelIds: string[];
  deadlineStr: string;
  recurEnabled: boolean;
  recurRule: RuleInput | null;
  seriesUntilStr: string;
  pendingSubtasks: string[];
}

function sameIds(a: string[], b: string[]): boolean {
  if (a.length !== b.length) return false;
  const s = new Set(b);
  return a.every((v) => s.has(v));
}

function sameRule(a: RuleInput | null, b: RuleInput | null): boolean {
  if (a === b) return true;
  if (!a || !b) return false;
  return toRRuleString(a) === toRRuleString(b);
}

function createDraftChanged(a: CreateDraftSnapshot, b: CreateDraftSnapshot): boolean {
  return a.title !== b.title
    || a.notes !== b.notes
    || a.dateStr !== b.dateStr
    || a.hour !== b.hour
    || a.minute !== b.minute
    || a.allDay !== b.allDay
    || a.duration !== b.duration
    || a.priority !== b.priority
    || !sameMatrixPos(a.matrixPos, b.matrixPos)
    || a.matrixHidden !== b.matrixHidden
    || a.customColor !== b.customColor
    || a.isEvent !== b.isEvent
    || a.location !== b.location
    || !sameOffsets(a.reminderOffsets, b.reminderOffsets)
    || a.tz !== b.tz
    || a.calendarId !== b.calendarId
    || !sameIds(a.topicIds, b.topicIds)
    || !sameIds(a.labelIds, b.labelIds)
    || a.deadlineStr !== b.deadlineStr
    || a.recurEnabled !== b.recurEnabled
    || !sameRule(a.recurRule, b.recurRule)
    || a.seriesUntilStr !== b.seriesUntilStr
    || !sameIds(a.pendingSubtasks, b.pendingSubtasks);
}

export function useTaskForm() {
  const open = useTaskDialogStore((s) => s.open);
  const editing = useTaskDialogStore((s) => s.editing);
  const defaultDate = useTaskDialogStore((s) => s.defaultDate);
  const defaultDuration = useTaskDialogStore((s) => s.defaultDuration);
  const defaultIsEvent = useTaskDialogStore((s) => s.defaultIsEvent);
  const defaultTitle = useTaskDialogStore((s) => s.defaultTitle);
  const close = useTaskDialogStore((s) => s.close);
  const add = useTasksStore((s) => s.add);
  const edit = useTasksStore((s) => s.edit);
  const remove = useTasksStore((s) => s.remove);
  const complete = useTasksStore((s) => s.complete);
  const loadRule = useTasksStore((s) => s.loadRule);
  const detachWithPatch = useTasksStore((s) => s.detachWithPatch);
  const editSeriesFromOccurrence = useTasksStore((s) => s.editSeriesFromOccurrence);
  const editSeriesUntil = useTasksStore((s) => s.editSeriesUntil);
  const rescheduleSeries = useTasksStore((s) => s.rescheduleSeries);
  const ask = useRecurringConfirmStore((s) => s.ask);

  const [title, setTitle] = useState("");
  const [notes, setNotes] = useState("");
  const [dateStr, setDateStr] = useState("");
  const [hour, setHour] = useState<number | null>(null);
  const [minute, setMinute] = useState<number | null>(null);
  const [allDay, setAllDay] = useState(false);
  const [duration, setDuration] = useState(0);
  const [priority, setPriority] = useState<Priority>("none");
  const [matrixPos, setMatrixPosState] = useState<MatrixPos | null>(null);
  const [matrixHidden, setMatrixHidden] = useState(false);
  const [customColor, setCustomColor] = useState<string | null>(null);
  const [isEvent, setIsEvent] = useState(false);
  const [reminderOffsets, setReminderOffsets] = useState<number[]>([]);
  const [tz, setTz] = useState<string | null>(null);
  const [calendarId, setCalendarId] = useState<string | null>(null);
  const [topicIds, setTopicIds] = useState<string[]>([]);
  const [labelIds, setLabelIds] = useState<string[]>([]);
  const [location, setLocation] = useState("");
  const [deadlineStr, setDeadlineStr] = useState("");
  const [recurEnabled, setRecurEnabled] = useState(false);
  const [recurRule, setRecurRule] = useState<RuleInput | null>(null);
  // Normalized rrule in effect when the form opened — the submit-time diff
  // baseline that tells a real cadence change from a no-op re-save.
  const [recurRuleRaw, setRecurRuleRaw] = useState<string | null>(null);
  const [seriesEndsAt, setSeriesEndsAt] = useState<Date | null>(null);
  const [seriesUntilStr, setSeriesUntilStr] = useState("");
  // Series-end field value as initially shown (diff baseline for seriesUntilStr).
  const [seriesUntilInitialStr, setSeriesUntilInitialStr] = useState("");
  const [pendingSubtasks, setPendingSubtasks] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [discardConfirmOpen, setDiscardConfirmOpen] = useState(false);
  const createBaselineRef = useRef<CreateDraftSnapshot | null>(null);

  // For virtual occurrences: route edits to the source task and cancel the series slot.
  const sourceId = editing ? (editing.virtual && editing.parent_id ? editing.parent_id : editing.id) : "";
  const occurrenceIso = editing?.virtual ? occurrenceRefOf(editing) : (editing?.due_at ?? null);

  // When the matrix system covers this entry, it is the source of truth for
  // priority (derived from the diagonal). `effectivePriority` recomputes from the
  // placement + deadline-derived urgency, so it tracks the deadline over time and
  // stays read-only in the flag controls. Unplaced/off-scope → the stored flag.
  const matrixScope = useSettingsStore((s) => s.matrixScope);
  const defaultTz = useSettingsStore((s) => s.defaultTz);
  const duEnabled = useSettingsStore((s) => s.deadlineUrgency);
  const duHigh = useSettingsStore((s) => s.deadlineUrgencyHighDays);
  const duMed = useSettingsStore((s) => s.deadlineUrgencyMediumDays);
  const today = useToday();
  const matrixInScope = matrixEnabledFor(matrixScope, isEvent);
  const effectivePriority = useMemo(() => {
    if (!matrixInScope || !matrixPos) return priority;
    const now = new Date(today);
    now.setHours(0, 0, 0, 0);
    const deadlineIso = formDeadlineIso({ isEvent, deadlineStr, dateStr, hour, minute, allDay, tz });
    // Zone precedence matches every other consumer (task tz → default → device),
    // incl. this form's own ghost column via `useDeadlineUrgencyValue(…, tz)` —
    // else the derived flag and the ghost could disagree near a day boundary.
    return effectivePriorityLevel(matrixPos, deadlineIso, now, tz ?? defaultTz ?? systemTimeZone(), {
      enabled: duEnabled, highWithinDays: duHigh, mediumWithinDays: duMed,
    });
  }, [matrixInScope, matrixPos, priority, today, isEvent, deadlineStr, dateStr, hour, minute, allDay, tz, defaultTz, duEnabled, duHigh, duMed]);

  const currentCreateDraft = (): CreateDraftSnapshot => ({
    title, notes, dateStr, hour, minute, allDay, duration, priority, matrixPos, matrixHidden, customColor, isEvent, location,
    reminderOffsets, tz, calendarId, topicIds, labelIds, deadlineStr, recurEnabled, recurRule, seriesUntilStr, pendingSubtasks,
  });

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setError(null);
    setPendingSubtasks([]);
    setDiscardConfirmOpen(false);
    createBaselineRef.current = null;
    if (editing) {
      setTitle(editing.title);
      setNotes(editing.notes);
      setCustomColor(editing.custom_color);
      setTz(editing.tz);
      if (editing.due_at) {
        // Show the wall-clock in the task's own zone (null = device-local).
        const wc = wallClockOf(editing.due_at, editing.tz);
        setDateStr(wc.dateStr);
        setHour(editing.all_day ? null : wc.hour);
        setMinute(editing.all_day ? null : wc.minute);
      } else {
        setDateStr(""); setHour(null); setMinute(null);
      }
      setAllDay(editing.all_day);
      setDuration(editing.duration_minutes);
      setPriority(editing.priority);
      setMatrixPosState(editing.matrix_pos);
      setMatrixHidden(editing.matrix_hidden);
      setIsEvent(editing.is_event);
      setReminderOffsets(editing.reminder_offsets);
      setCalendarId(editing.calendar_id);
      setTopicIds(editing.topic_ids);
      setLabelIds(editing.label_ids);
      setLocation(editing.location);
      const editDeadlineStr = editing.deadline_at ? toLocalDate(new Date(editing.deadline_at)) : "";
      setDeadlineStr(editDeadlineStr);
      // Baseline for edit-mode dirty detection (recur fields patched after the
      // rule loads, below) — so closing after a change prompts a discard confirm.
      const editWc = editing.due_at ? wallClockOf(editing.due_at, editing.tz) : null;
      createBaselineRef.current = {
        title: editing.title, notes: editing.notes,
        dateStr: editWc ? editWc.dateStr : "",
        hour: editing.all_day ? null : (editWc ? editWc.hour : null),
        minute: editing.all_day ? null : (editWc ? editWc.minute : null),
        allDay: editing.all_day, duration: editing.duration_minutes, priority: editing.priority,
        matrixPos: editing.matrix_pos, matrixHidden: editing.matrix_hidden,
        customColor: editing.custom_color, isEvent: editing.is_event, location: editing.location,
        reminderOffsets: editing.reminder_offsets, tz: editing.tz, calendarId: editing.calendar_id,
        topicIds: editing.topic_ids, labelIds: editing.label_ids, deadlineStr: editDeadlineStr,
        recurEnabled: !!editing.recurrence_id, recurRule: null, seriesUntilStr: "", pendingSubtasks: [],
      };
      if (editing.recurrence_id) {
        // Reset synchronously so a previous open's recurrence can't leak into
        // this one while the rule loads (the picker used to show stale state
        // for virtual occurrences, and the cadence change was then dropped).
        setRecurEnabled(true);
        setRecurRule(null);
        setRecurRuleRaw(null);
        setSeriesEndsAt(null);
        setSeriesUntilStr("");
        setSeriesUntilInitialStr("");
        void loadRule(editing.recurrence_id).then((r) => {
          if (!r || cancelled) return;
          // The calendar field shows the last actual occurrence date (what the
          // user thinks of as "the series ends on …"), matching the text label.
          // `rule.until` is an internal EXCLUSIVE boundary that, after a split,
          // sits one cadence past the last occurrence — showing it raw made the
          // field and the "Series ends" text disagree.
          const last = lastOccurrence(r);
          setSeriesEndsAt(last);
          const lastStr = last ? toLocalDate(last) : "";
          setSeriesUntilStr(lastStr);
          setSeriesUntilInitialStr(lastStr);
          // Show the rrule in effect AT THIS OCCURRENCE (a thisandfuture
          // override may have reshaped it from the base rule).
          const ovs = useTasksStore.getState().overrides[r.id] ?? [];
          const eff = occurrenceIso ? resolveEffectiveRrule(r, occurrenceIso, ovs) : r.rrule;
          const parsed = parseRRule(eff);
          setRecurRule(parsed);
          setRecurRuleRaw(toRRuleString(parsed));
          // Fold the loaded recurrence into the edit baseline so the async load
          // itself isn't seen as an unsaved change.
          if (createBaselineRef.current) {
            createBaselineRef.current.recurRule = parsed;
            createBaselineRef.current.seriesUntilStr = lastStr;
          }
        });
      } else {
        setRecurEnabled(false); setRecurRule(null); setRecurRuleRaw(null); setSeriesEndsAt(null); setSeriesUntilStr(""); setSeriesUntilInitialStr("");
      }
    } else {
      const settings = useSettingsStore.getState();
      const nextIsEvent = defaultIsEvent ?? settings.defaultEntryIsEvent;
      const nextReminderOffsets = defaultReminderOffsetsForNewItem(nextIsEvent, settings.defaultReminderMinutes, settings.defaultEventReminderMinutes);
      let nextDateStr = "";
      let nextHour: number | null = null;
      let nextMinute: number | null = null;
      let nextAllDay = false;
      const nextDuration = defaultDuration === 1440 ? 0 : (defaultDuration ?? settings.defaultDurationMin);
      setTitle(defaultTitle ?? ""); setNotes(""); setCustomColor(null); setIsEvent(nextIsEvent); setReminderOffsets(nextReminderOffsets); setTz(settings.defaultTz); setCalendarId(settings.defaultCalendarId); setTopicIds([]); setLabelIds([]); setLocation(""); setDeadlineStr(""); setSeriesUntilStr("");
      if (defaultDate) {
        nextDateStr = toLocalDate(defaultDate);
        setDateStr(nextDateStr);
        if (defaultDuration === 1440) {
          nextAllDay = true;
          setAllDay(true); setHour(null); setMinute(null);
        } else {
          const hasTime = !(defaultDate.getHours() === 0 && defaultDate.getMinutes() === 0);
          nextHour = hasTime ? defaultDate.getHours() : 9;
          nextMinute = hasTime ? defaultDate.getMinutes() : 0;
          setHour(nextHour);
          setMinute(nextMinute);
          setAllDay(false);
        }
      } else {
        setDateStr(""); setHour(null); setMinute(null); setAllDay(false);
      }
      setDuration(nextDuration);
      setPriority("none"); setMatrixPosState(null); setMatrixHidden(false); setRecurEnabled(false); setRecurRule(null); setRecurRuleRaw(null); setSeriesEndsAt(null); setSeriesUntilInitialStr("");
      createBaselineRef.current = {
        title: "", notes: "", dateStr: nextDateStr, hour: nextHour, minute: nextMinute, allDay: nextAllDay,
        duration: nextDuration, priority: "none", matrixPos: null, matrixHidden: false, customColor: null, isEvent: nextIsEvent, location: "",
        reminderOffsets: nextReminderOffsets, tz: settings.defaultTz, calendarId: settings.defaultCalendarId,
        topicIds: [], labelIds: [], deadlineStr: "", recurEnabled: false, recurRule: null, seriesUntilStr: "",
        pendingSubtasks: [],
      };
    }
    return () => { cancelled = true; };
  }, [open, editing, occurrenceIso, defaultDate, defaultDuration, defaultIsEvent, defaultTitle, loadRule]);

  async function handleComplete() {
    if (!editing) return;
    await complete(sourceId, editing.status !== "done");
    close();
  }

  async function submit() {
    setError(null);
    setSubmitting(true);
    // One gesture, even though the form may issue several store mutations (main
    // task + subtasks + whole-series property edits): the outer scope snapshots
    // once and shows a single Undo toast; the inner store actions are suppressed.
    const outer = await beginUndoable(editing ? "Edit task" : "Create task");
    try {
      const shouldClose = await submitTaskForm({
        editing, sourceId, occurrenceIso,
        values: { title, notes, dateStr, hour, minute, allDay, duration, priority: effectivePriority, matrixPos, matrixHidden, customColor, isEvent, location, reminderOffsets, tz, calendarId, topicIds, labelIds, deadlineStr, recurEnabled, recurRule, recurRuleRaw, seriesUntilStr, seriesUntilInitialStr, pendingSubtasks },
        actions: { add, edit, detachWithPatch, editSeriesFromOccurrence, editSeriesUntil, rescheduleSeries, loadRule },
        ask,
      });
      if (outer && shouldClose) {
        toast.withAction("info", tr(editing ? "toast.task.updated" : "toast.task.created"), undoAction());
      }
      if (shouldClose) close();
    } catch (e) {
      console.error("Task save failed:", e);
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      endUndoable();
      setSubmitting(false);
    }
  }

  async function deleteIt() {
    if (!editing) return;
    await remove(sourceId);
    close();
  }

  // When the matrix system is in scope, priority is DERIVED from the matrix and
  // the flags are read-only — a flag tap is ignored. Otherwise the flag is the
  // editable priority (no matrix in play).
  function setPriorityToggle(p: Priority) {
    if (matrixInScope) return;
    setPriority(p);
  }

  // Placing/moving the dot (or toggling auto) just stores the position; priority
  // is derived reactively via `effectivePriority`. Clearing drops the mirrored
  // flag WITH it: in scope the matrix is the sole source of priority, so an
  // unplaced entry has none — keeping the last snapshot would strand a
  // priority the read-only flag rail can't edit. Matches the board's tray drop.
  function setMatrixPos(pos: MatrixPos | null) {
    setMatrixPosState(pos);
    if (!pos && matrixInScope) setPriority("none");
  }

  function setAllDayToggle(checked: boolean) {
    setAllDay(checked);
    if (checked) { setHour(null); setMinute(null); }
  }

  function setIsEventToggle(checked: boolean) {
    setIsEvent(checked);
    if (checked) setDeadlineStr(""); // events carry no deadline
    if (!editing) {
      setReminderOffsets((current) => reminderOffsetsForNewItemKindToggle(
        checked,
        current,
        useSettingsStore.getState().defaultReminderMinutes,
        useSettingsStore.getState().defaultEventReminderMinutes,
      ));
    }
  }

  function hasUnsavedChanges(): boolean {
    return !!createBaselineRef.current && createDraftChanged(currentCreateDraft(), createBaselineRef.current);
  }

  function requestClose() {
    if (hasUnsavedChanges()) {
      setDiscardConfirmOpen(true);
      return;
    }
    close();
  }

  function cancelDiscard() {
    setDiscardConfirmOpen(false);
  }

  function confirmDiscard() {
    setDiscardConfirmOpen(false);
    close();
  }

  return {
    open, editing, close,
    title, setTitle, notes, setNotes,
    dateStr, setDateStr, allDay, setAllDay: setAllDayToggle,
    hour, setHour, minute, setMinute,
    duration, setDuration,
    priority: effectivePriority, setPriority: setPriorityToggle,
    /** True when the matrix system governs this entry's priority (flags read-only). */
    matrixInScope,
    matrixPos, setMatrixPos,
    /** Keep this entry off the Priorities board/queue (whole-series). */
    matrixHidden, setMatrixHidden,
    customColor, setCustomColor,
    isEvent, setIsEvent: setIsEventToggle,
    reminderOffsets, setReminderOffsets,
    tz, setTz,
    calendarId, setCalendarId,
    topicIds, setTopicIds,
    labelIds, setLabelIds,
    location, setLocation,
    pendingSubtasks, setPendingSubtasks,
    deadlineStr, setDeadlineStr,
    recurEnabled, setRecurEnabled, recurRule, setRecurRule,
    seriesEndsAt, seriesUntilStr, setSeriesUntilStr,
    error, submitting,
    submit, deleteIt, handleComplete, requestClose, hasUnsavedChanges,
    discardConfirmOpen, cancelDiscard, confirmDiscard,
  };
}
